import styled from 'styled-components';

const Wrap = styled.nav`
  margin-top: 40px;
  padding-top: 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const Summary = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.type.bodyXs};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.mutedForeground};
  text-align: center;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const NavBtn = styled.button`
  padding: 8px 14px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: transparent;
  font-size: ${({ theme }) => theme.type.bodyXs};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  font-family: inherit;

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }

  &:not(:disabled):hover {
    border-color: ${({ theme }) => theme.colors.foreground};
  }
`;

const PageBtn = styled.button`
  min-width: 36px;
  height: 36px;
  padding: 0 8px;
  border: 1px solid
    ${({ $active, theme }) => ($active ? theme.colors.foreground : theme.colors.border)};
  background: ${({ $active, theme }) => ($active ? theme.colors.foreground : 'transparent')};
  color: ${({ $active, theme }) => ($active ? theme.colors.background : theme.colors.foreground)};
  font-size: ${({ theme }) => theme.type.bodySm};
  cursor: pointer;
  font-family: inherit;

  &:disabled {
    cursor: default;
  }

  &:not(:disabled):hover {
    border-color: ${({ theme }) => theme.colors.foreground};
  }
`;

const Ellipsis = styled.span`
  padding: 0 4px;
  color: ${({ theme }) => theme.colors.mutedForeground};
  font-size: ${({ theme }) => theme.type.bodySm};
`;

/** @returns {number[]} */
function pageWindow(current, total, max = 7) {
  if (total <= max) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const half = Math.floor(max / 2);
  let start = Math.max(1, current - half);
  let end = Math.min(total, start + max - 1);
  start = Math.max(1, end - max + 1);
  const pages = [];
  for (let i = start; i <= end; i += 1) pages.push(i);
  return pages;
}

export default function JournalPagination({
  page,
  pages,
  total,
  limit,
  onPageChange,
  disabled,
  summaryLabel,
  previousLabel,
  nextLabel,
}) {
  const totalPages = Math.max(1, pages || 1);
  const current = Math.min(Math.max(1, page || 1), totalPages);

  if (totalPages <= 1 && (!total || total <= limit)) {
    return null;
  }

  const from = total ? (current - 1) * limit + 1 : 0;
  const to = total ? Math.min(current * limit, total) : 0;
  const windowPages = pageWindow(current, totalPages);

  return (
    <Wrap aria-label="Journal pagination">
      {summaryLabel ? <Summary>{summaryLabel({ from, to, total, current, totalPages })}</Summary> : null}
      <Row>
        <NavBtn type="button" disabled={disabled || current <= 1} onClick={() => onPageChange(current - 1)}>
          {previousLabel}
        </NavBtn>
        {windowPages[0] > 1 ? (
          <>
            <PageBtn type="button" disabled={disabled} onClick={() => onPageChange(1)}>
              1
            </PageBtn>
            {windowPages[0] > 2 ? <Ellipsis>…</Ellipsis> : null}
          </>
        ) : null}
        {windowPages.map((n) => (
          <PageBtn
            key={n}
            type="button"
            $active={n === current}
            disabled={disabled || n === current}
            onClick={() => onPageChange(n)}
            aria-current={n === current ? 'page' : undefined}
          >
            {n}
          </PageBtn>
        ))}
        {windowPages[windowPages.length - 1] < totalPages ? (
          <>
            {windowPages[windowPages.length - 1] < totalPages - 1 ? <Ellipsis>…</Ellipsis> : null}
            <PageBtn type="button" disabled={disabled} onClick={() => onPageChange(totalPages)}>
              {totalPages}
            </PageBtn>
          </>
        ) : null}
        <NavBtn
          type="button"
          disabled={disabled || current >= totalPages}
          onClick={() => onPageChange(current + 1)}
        >
          {nextLabel}
        </NavBtn>
      </Row>
    </Wrap>
  );
}
